import React, { useMemo } from 'react';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Badge } from '../../../../../../../components/ui/badge';

interface WeldParametersTableProps {
    jobData: any;
}

interface HeaderColumn {
    fullHeader: string; // "1 : R1X1 : 40"
    group: string;
    id: string;
    angle: string;
    torches: string[]; // ["LEAD", "TRAIL"]
}

interface Parameter {
    name: string;
    values: any[];
}

interface Category {
    name: string;
    parameters: Parameter[];
}

const WeldParametersTable: React.FC<WeldParametersTableProps> = ({ jobData }) => {
    // Extract and parse weld parameters data
    const { headers, categories } = useMemo(() => {
        const weldParams = jobData?.parsed_json_data?.['WELD PARAMETERS'];
        
        if (!weldParams) {
            return { headers: [], categories: [] };
        }

        console.log('weldParams.values', weldParams.values);

        // Parse headers
        const parsedHeaders: HeaderColumn[] = [];
        if (Array.isArray(weldParams.header)) {
            weldParams.header.forEach((headerObj: any) => {
                const headerKey = Object.keys(headerObj)[0];
                const torches = headerObj[headerKey];
                
                // Parse "1 : R1X1 : 40" format
                const parts = headerKey.split(' : ');
                if (parts.length >= 3) {
                    parsedHeaders.push({
                        fullHeader: headerKey,
                        group: parts[0].trim(),
                        id: parts[1].trim(),
                        angle: parts[2].trim(),
                        torches: Array.isArray(torches) ? torches : []
                    });
                }
            });
        }

        // Parse categories and parameters
        // Check if data is under 'values' key or directly under weldParams
        const dataSource = weldParams.values || weldParams;
        const parsedCategories: Category[] = [];
        
        Object.keys(dataSource).forEach(key => {
            if (key === 'header') return; // Skip header
            
            const categoryData = dataSource[key];
            if (Array.isArray(categoryData)) {
                const parameters: Parameter[] = [];
                
                categoryData.forEach((paramObj: any) => {
                    Object.keys(paramObj).forEach(paramName => {
                        // Skip 8th and 9th parameters for Pass category
                        if (key === 'Pass' && (
                            paramName === 'Pass Name By Group' || 
                            paramName === 'Pass Pendant Name By Group'
                        )) {
                            return; // Skip these parameters
                        }
                        
                        const rawValue = paramObj[paramName];
                        
                        // Handle different value formats:
                        // 1. Direct array: ["value1", "value2", ...]
                        // 2. Array with unit: [{ "Unit": "deg" }, ["value1", "value2", ...]]
                        // 3. Object with nested array and Unit
                        
                        let finalValues: any[] = [];
                        let unit = '';
                        
                        if (Array.isArray(rawValue)) {
                            // Check if first element is a Unit object
                            if (rawValue.length >= 2 && 
                                rawValue[0] && 
                                typeof rawValue[0] === 'object' && 
                                !Array.isArray(rawValue[0]) &&
                                rawValue[0].Unit &&
                                Array.isArray(rawValue[1])) {
                                // Format: [{ "Unit": "deg" }, ["20", "20", ...]]
                                unit = rawValue[0].Unit;
                                finalValues = rawValue[1];
                            } else {
                                // Direct array format
                                finalValues = rawValue;
                            }
                        } else if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
                            // Object format: check if it has a Unit and an array
                            const valueKeys = Object.keys(rawValue);
                            const arrayKey = valueKeys.find(k => Array.isArray(rawValue[k]));
                            if (arrayKey) {
                                unit = rawValue.Unit || '';
                                finalValues = rawValue[arrayKey];
                            }
                        }
                        
                        if (finalValues.length > 0) {
                            const displayName = unit ? `${paramName} (${unit})` : paramName;
                            
                            // Convert any objects in values to strings
                            const cleanedValues = finalValues.map(val => {
                                if (val && typeof val === 'object' && !Array.isArray(val)) {
                                    // Object like { "BAA": "Hot Pass 1" }
                                    const keys = Object.keys(val);
                                    if (keys.length > 0) {
                                        // Return the first value or a formatted string
                                        return val[keys[0]] || JSON.stringify(val);
                                    }
                                    return '';
                                }
                                return val;
                            });
                            
                            parameters.push({
                                name: displayName,
                                values: cleanedValues
                            });
                        }
                    });
                });
                
                if (parameters.length > 0) {
                    parsedCategories.push({
                        name: key,
                        parameters: parameters
                    });
                }
            }
        });

        return { headers: parsedHeaders, categories: parsedCategories };
    }, [jobData]);

    const getStatusBadge = (value: string) => {
        const valueStr = String(value).toLowerCase();
        
        if (valueStr === 'enabled' || valueStr === 'enable' || valueStr === 'yes') {
            return <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs">Enabled</Badge>;
        } else if (valueStr === 'disabled' || valueStr === 'disable' || valueStr === 'no') {
            return <Badge variant="destructive" className="text-xs">Disabled</Badge>;
        }
        return <span className="text-sm">{value}</span>;
    };

    const shouldShowBadge = (paramName: string) => {
        const name = paramName.toLowerCase();
        return name.includes('enable') || 
               name.includes('pull') || 
               name.includes('select') ||
               name.includes('direction');
    };

    if (!headers.length || !categories.length) {
        return (
            <div className="mt-2 w-full">
                <Card className="bg-white border border-gray-200">
                    <CardContent className="p-8 text-center text-gray-500">
                        No weld parameters data available
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="mt-2 w-full h-full flex flex-col">
            <Card className="bg-white border border-gray-200 h-full flex flex-col">
                <CardContent className="p-1 w-full h-full flex flex-col">
                    {/* Single scroll container - required for sticky positioning */}
                    <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <table className="min-w-full w-full caption-bottom text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead className="sticky top-0 bg-white z-20" style={{ boxShadow: '0 2px 0 0 rgba(209, 213, 219, 1)' }}>
                                {/* First Header Row - Group:ID:Angle */}
                                <tr>
                                    <th 
                                        rowSpan={2}
                                        className="min-w-[200px] max-w-[200px] bg-gray-100 px-3 py-3 text-sm font-bold text-gray-800 text-left sticky left-0 z-30 border-r-2 border-gray-300 border-b-2 border-gray-300 h-12 align-middle"
                                    >
                                       
                                    </th>
                                    {headers.map((header, index) => (
                                        <th 
                                            key={index}
                                            colSpan={header.torches.length}
                                            className="min-w-[120px] bg-gray-100 px-2 py-3 text-sm font-bold text-gray-800 text-center border-r border-gray-300 border-b border-gray-300 h-12 align-middle"
                                        >
                                            {header.group} : {header.id} : {header.angle}
                                        </th>
                                    ))}
                                </tr>
                                {/* Second Header Row - LEAD/TRAIL - This is the last row in thead, so it needs bottom border */}
                                <tr>
                                    {headers.map((header, index) => (
                                        <React.Fragment key={index}>
                                            {header.torches.map((torch, torchIndex) => {
                                                const isLead = torch === 'LEAD';
                                                const bgColor = isLead ? 'bg-blue-50' : 'bg-orange-50';
                                                const textColor = isLead ? 'text-blue-700' : 'text-orange-700';
                                                return (
                                                    <th 
                                                        key={torchIndex}
                                                        className={`min-w-[100px] ${bgColor} px-2 py-2 text-xs font-semibold ${textColor} text-center border-r border-gray-300 border-b-2 border-gray-300 h-12 align-middle`}
                                                    >
                                                        {torch}
                                                    </th>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="[&_tr:last-child]:border-0">
                                    {categories.map((category, categoryIndex) => (
                                        <React.Fragment key={categoryIndex}>
                                            {/* Category Header Row */}
                                            <tr className="bg-gray-200 transition-colors hover:bg-gray-50/50">
                                                {/* First column - Category name, sticky left */}
                                                <td 
                                                    className="px-3 py-2 text-sm font-bold text-gray-800 bg-gray-200 sticky left-0 z-10 border-r-2 border-gray-300 border-b-2 border-gray-300 align-middle"
                                                    style={{ top: '96px' }}
                                                >
                                                    {category.name}
                                                </td>
                                                {/* Remaining columns - one for each torch */}
                                                {headers.map((header, headerIndex) => (
                                                    <React.Fragment key={headerIndex}>
                                                        {header.torches.map((torch, torchIndex) => {
                                                            return (
                                                                <td 
                                                                    key={torchIndex}
                                                                    className="px-2 py-2 text-sm font-bold text-gray-800 bg-gray-200 align-middle"
                                                                >
                                                                    {/* Empty cell with same styling */}
                                                                </td>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                            </tr>

                                            {/* Parameter Rows */}
                                            {category.parameters.map((param, paramIndex) => {
                                                // Calculate which columns are LEAD or TRAIL for coloring
                                                let columnIndex = 0;
                                                return (
                                                    <tr key={paramIndex} className="border-b border-gray-200 transition-colors hover:bg-gray-50/50">
                                                        <td className="w-[200px] min-w-[200px] max-w-[200px] bg-white px-3 py-2 text-sm font-medium text-gray-700 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                                            {param.name}
                                                        </td>
                                                        {headers.map((header, headerIndex) => (
                                                            <React.Fragment key={headerIndex}>
                                                                {header.torches.map((torch, torchIndex) => {
                                                                    const value = param.values[columnIndex++];
                                                                    const isLead = torch === 'LEAD';
                                                                    const bgColor = isLead ? 'bg-blue-50' : 'bg-orange-50';
                                                                    return (
                                                                        <td 
                                                                            key={torchIndex}
                                                                            className={`min-w-[100px] px-2 py-2 text-sm text-center text-gray-700 ${bgColor} border-r border-gray-300 border-b border-gray-200 font-mono align-middle`}
                                                                        >
                                                                            {shouldShowBadge(param.name) ? 
                                                                                getStatusBadge(value) : 
                                                                                <span className="text-sm">{value}</span>
                                                                            }
                                                                        </td>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default WeldParametersTable;
